import { useState } from "react";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { FormattedMessage, useIntl } from "react-intl";
import { useParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { useStateMachine } from "little-state-machine";

import { shipmentQueryKeys } from "api/queries/shipments/shipmentQueryKeys";
import { useGetShipmentDetails } from "api/queries/shipments/useGetShipmentDetails";
import { useGetTrackingReferences } from "api/queries/shipments/useGetTrackingReferences";
import usePermissions from "hooks/usePermissions";
import testIds from "constants/testIds";
import { updateBackgroundLoadingState } from "store/store";
import PickupDeliveryProofDialog from "../PickupDeliveryProofDialog/PickupDeliveryProofDialog";
import { canShowProofOfPickup } from "./canShowProofOfPickup";

export type ProofOfPickupButtonProps = Readonly<{
  isOffPlatform: boolean;
}>;

export default function ProofOfPickupButton({ isOffPlatform }: ProofOfPickupButtonProps) {
  const intl = useIntl();
  const queryClient = useQueryClient();
  const { actions } = useStateMachine({ updateBackgroundLoadingState });
  const { id } = useParams();
  const { permissions } = usePermissions();
  const { user_type } = JSON.parse(permissions || "{}");
  const [showDialog, setShowDialog] = useState(false);

  const { data: shipmentDetails = {} } = useGetShipmentDetails(id!, true, {
    enabled: !!id,
    refetchOnMount: false,
  });

  const { data: trackingReferencesData } = useGetTrackingReferences(id!, {
    enabled: !!id,
    refetchOnMount: false,
  });

  const isPoPSubmitted = Boolean(shipmentDetails?.schedule?.is_proof_of_pickup_submitted);
  const shipmentStatus = shipmentDetails?.organization?.shipment_status;
  const canSubmitWithoutTracking = trackingReferencesData?.tracking_settings?.can_submit_pod_without_tracking;

  const isVisible = canShowProofOfPickup({
    userType: user_type,
    shipmentStatus,
    isPoPSubmitted,
    isOffPlatform,
  });

  if (!isVisible) return null;

  const refetchQueries = () => {
    actions.updateBackgroundLoadingState(true);
    queryClient.invalidateQueries({ queryKey: shipmentQueryKeys.shipmentBasicDetails(id!) });
    queryClient.invalidateQueries({ queryKey: shipmentQueryKeys.shipmentDetails(id!) });
    queryClient.invalidateQueries({ queryKey: shipmentQueryKeys.shipmentGeoDataDetails(id!) });
  };

  const handleOpenDialog = () => {
    if (canSubmitWithoutTracking) {
      setShowDialog(true);
    }
  };

  return (
    <>
      <OverlayTrigger
        placement="bottom"
        delay={{ show: 250, hide: 0 }}
        overlay={
          !canSubmitWithoutTracking ? (
            <Tooltip id="pop-tooltip">
              {intl.formatMessage({
                id: "tracking.components.podDialog.cannotSubmit",
                defaultMessage: "Cannot submit POP or POD without tracking number",
              })}
            </Tooltip>
          ) : (
            <span />
          )
        }
      >
        <button
          type="button"
          className="cta-button outline"
          onClick={handleOpenDialog}
          disabled={!canSubmitWithoutTracking}
          data-testid={testIds.SHIPMENT_DASHBOARD_PROOF_OF_PICKUP_BUTTON}
        >
          <FormattedMessage id="components.molecules.top-nav.proofOfPickup" defaultMessage="Proof of pickup" />
        </button>
      </OverlayTrigger>
      <PickupDeliveryProofDialog
        show={showDialog}
        onHide={() => setShowDialog(false)}
        isPoPSubmitted={isPoPSubmitted}
        refetchFn={refetchQueries}
        isOffPlatform={isOffPlatform}
        mode="pickupOnly"
      />
    </>
  );
}
